import { z } from 'zod';
import { TenantIdSchema, IsoTimestampSchema, RutSchema } from './common.js';
import { FeatureFlagsPatchSchema } from './feature-flags.js';

/**
 * Condominio — raíz agregada de un tenant.
 * Representa un edificio o complejo bajo una comunidad de copropietarios.
 */
export const CondominioSchema = z.object({
  id: TenantIdSchema,
  rut: RutSchema.describe('RUT del condominio (comunidad de copropietarios)'),
  nombre: z.string().min(3).max(200),
  direccion: z.object({
    calle: z.string().min(3).max(200),
    numero: z.string().min(1).max(20),
    comuna: z.string().min(2).max(100),
    region: z.string().min(2).max(100),
  }),
  /** Moneda base de la contabilidad. MVP: solo CLP. */
  moneda: z.literal('CLP').default('CLP'),
  /** Tasa de interés mensual por mora en %. Ej: 1.5 = 1.5%/mes. */
  tasaMoraMensualPct: z.number().min(0).max(10).default(1.5),
  /** Día de vencimiento default para gastos comunes (1-28). */
  diaVencimientoDefault: z.number().int().min(1).max(28).default(10),
  /** Porcentaje del gasto común destinado al fondo de reserva. */
  fondoReservaPorcentaje: z.number().min(0).max(100).default(5),
  /** PIN de 4 dígitos para que el conserje marque pases de visita como usados. */
  pinConserje: z.string().length(4).regex(/^\d{4}$/).optional(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});
export type Condominio = z.infer<typeof CondominioSchema>;

export const CreateCondominioRequestSchema = CondominioSchema.pick({
  rut: true,
  nombre: true,
  direccion: true,
  tasaMoraMensualPct: true,
  diaVencimientoDefault: true,
  fondoReservaPorcentaje: true,
}).partial({ tasaMoraMensualPct: true, diaVencimientoDefault: true, fondoReservaPorcentaje: true }).extend({
  /** Optional feature-flag overrides applied on top of defaults at creation time. */
  featureFlags: FeatureFlagsPatchSchema.optional(),
});
export type CreateCondominioRequest = z.infer<typeof CreateCondominioRequestSchema>;

export const UpdateCondominioRequestSchema = CreateCondominioRequestSchema.partial();
export type UpdateCondominioRequest = z.infer<typeof UpdateCondominioRequestSchema>;
